'use client';
import noop from 'lodash/noop';
import type { ComponentType, PropsWithChildren } from 'react';
import { createContext, memo, useContext, useEffect, useState } from 'react';
import { Remount } from '../../../components/Remount';
import { compose } from '../../../utils/compose';
import { InterfaceModes } from '../../const';
import { usePageInstanceId } from '../../context/PageInstanceIdProvider';
import { useRegistryContext } from '../../context/RegistryProvider';
import { APP_MANAGER_ID, PAGE_MANAGER_FALLBACK, scopeIdOf } from '../../pulse/scopeRegistry';
import { useGetGlobalAppStateStoreState } from '../app/GlobalAppStateStore';
import { getContextualDialogsForBlocks } from '../app/GlobalAppStateStore/utils/getContextualDialogsForBlocks';
import { getInitialPageVariablesState } from '../app/GlobalAppStateStore/utils/getInitialPageVariablesState';
import { useDataSourceRecordStore } from '../DataSourceRecordStore';
import { subscribeDraggableBlocks } from '../instance/services/DraggableBlocksService';
import { createGlobalStateStore } from './createGlobalStateStore';
import {
  createGlobalStateStoreManager,
  type GlobalStateStoreInstance,
  type GlobalStateStoreManager,
} from './GlobalStateStoreManager';
import { useGlobalStateStoreImperativeRef } from './hooks/useGlobalStateStoreImperativeRef';
import { useSyncGlobalStateProps } from './hooks/useSyncGlobalStateProps';
import { PagePulseScopeRegistrarGate } from './PagePulseScopeRegistrarGate';
import { createGlobalStateStoreSelectors } from './selectors';
import type {
  BaseGlobalStateStoreProviderProps,
  GlobalStateStoreInitialValue,
  WithRemountProps,
} from './types';
import { getInitialBlockState } from './utils/getInitialBlockState';

export const GlobalStateStoreManagerContext = createContext<GlobalStateStoreManager | null>(null);

export const useGlobalStateStoreManager = () => {
  const manager = useContext(GlobalStateStoreManagerContext);
  if (!manager) {
    throw new Error('Missing GlobalStateStoreProvider');
  }
  return manager;
};

export const useGlobalStateStore = (): GlobalStateStoreInstance => {
  const manager = useGlobalStateStoreManager();
  const instanceId = usePageInstanceId();
  return manager.getStore(instanceId);
};

function BaseGlobalStateStoreProvider({
  blocks,
  pageVariables,
  pageInputs,
  componentOverrides,
  pageParams,
  children,
  pageFunctions,
  globalStateStoreRef,
  remount,
  getDependencyGraphActions,
  interfacePageId,
  interfacePageName,
}: PropsWithChildren<BaseGlobalStateStoreProviderProps>) {
  const getDataSourceRecordsStore = useDataSourceRecordStore().getState;
  const { registry } = useRegistryContext();
  const { getGlobalAppStateStoreState } = useGetGlobalAppStateStoreState();
  const parentPageManager = useContext(GlobalStateStoreManagerContext);

  const [manager] = useState(() => {
    const appState = getGlobalAppStateStoreState();
    const managerId = interfacePageId ?? PAGE_MANAGER_FALLBACK;
    const baseScopeId = scopeIdOf(managerId, null);
    const capture = appState.mode !== InterfaceModes.RUNNER;
    // merge override deltas onto authored blocks before computing initial state so child blocks hydrate with the parent TC's override applied on first render
    const initialBlocks = blocks
      ? getInitialBlockState({ blocks, registry, componentOverrides })
      : {};
    const initialValue: GlobalStateStoreInitialValue = {
      pageInputs: pageInputs ?? {},
      pageParams: pageParams ?? {},
      blocks: initialBlocks,
      pageVariables: {},
      pageFunctions: pageFunctions ?? {},
      contextualDialogForBlocks: getContextualDialogsForBlocks(blocks ?? {}, registry),
      // onOutputChange / emitPageEvent are only wired by inline page instances (Module, TemplateComponent)
      // via manager.getStore(id)'s seeded initial. Base store uses noop / undefined so the shared store
      // shape stays consistent; action dispatch reads the per-instance value by instanceId.
      onOutputChange: noop,
    };

    if (pageVariables) {
      initialValue.pageVariables = getInitialPageVariablesState({
        pageVariables,
        blocks: initialBlocks,
        pageInputs: initialValue.pageInputs,
        pageParams: initialValue.pageParams,
        appState,
      });
    }

    const baseStore = createGlobalStateStoreSelectors(
      createGlobalStateStore({
        initial: initialValue,
        getDataSourceRecordsStore,
        remount,
        scopeId: baseScopeId,
        capture,
      }),
    );

    const createStore = (instanceId: string, initial: GlobalStateStoreInitialValue) =>
      createGlobalStateStoreSelectors(
        createGlobalStateStore({
          initial,
          getDataSourceRecordsStore,
          remount,
          scopeId: scopeIdOf(managerId, instanceId),
          capture,
        }),
      );
    return createGlobalStateStoreManager(baseStore, createStore, { managerId, interfacePageId });
  });

  const store = manager.baseStore;

  useSyncGlobalStateProps({
    store,
    blocks,
    pageInputs,
    pageParams,
    componentOverrides,
    registry,
    getDependencyGraphActions,
  });

  // exposes updateBlocks / updatePageVariables on globalStateStoreRef for imperative flows (builder edits, hot-swap); carries componentOverrides so added/changed blocks hydrate with overrides applied
  useGlobalStateStoreImperativeRef({
    store,
    registry,
    globalStateStoreRef,
    getGlobalAppStateStoreState,
    componentOverrides,
  });

  // mirror INSTANCE_SERVICES for the base page: inline page-instances run the draggable-blocks service via createPageInstance, but the outer page has no such lifecycle — subscribe here so base-page draggable Cards populate `draggableBlockIds` too
  useEffect(() => {
    const ac = new AbortController();
    const unsubscribe = subscribeDraggableBlocks(store, ac.signal);
    return () => {
      ac.abort();
      unsubscribe();
    };
  }, [store]);

  const { managerId } = manager;
  const parentManagerId = parentPageManager?.managerId ?? APP_MANAGER_ID;
  const parentScopeId = parentManagerId ? scopeIdOf(parentManagerId, null) : undefined;

  return (
    <GlobalStateStoreManagerContext.Provider value={manager}>
      <PagePulseScopeRegistrarGate
        interfacePageId={interfacePageId}
        interfacePageName={interfacePageName ?? ''}
        managerId={managerId}
        parentScopeId={parentScopeId}
        store={store}
      />
      {children}
    </GlobalStateStoreManagerContext.Provider>
  );
}

const withRemount = (
  Component: ComponentType<PropsWithChildren<BaseGlobalStateStoreProviderProps>>,
) => {
  function Wrapper(props: PropsWithChildren<BaseGlobalStateStoreProviderProps>) {
    return <Remount>{(remount) => <Component {...props} remount={remount} />}</Remount>;
  }

  return Wrapper;
};

const GlobalStateStoreProvider = compose<ComponentType<PropsWithChildren<WithRemountProps>>>(
  memo,
  withRemount,
)(BaseGlobalStateStoreProvider);

export { GlobalStateStoreProvider };
